import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { KeyRound, CheckCircle2, Receipt } from "lucide-react";

/**
 * Create (or attach) a login user for a payroll worker so they can be
 * selected as a colony ticket watcher and, later, log in. Idempotent —
 * calling again on the same worker just refreshes the linked user
 * record without creating a duplicate.
 */
export default function WorkerUserDialog({ worker, onOpenChange, onSaved, onManagerToggled }) {
  const open = !!worker;
  const already = !!worker?.user_id;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [managerBusy, setManagerBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(worker?.email || "");
      setPassword("");
      setSendInvite(true);
      setIsManager(worker?.is_worker_manager === true);
    }
  }, [open, worker]);

  const toggleManager = async (next) => {
    setManagerBusy(true);
    try {
      await api.patch(`/mgmt/worker-users/${worker.user_id}/manager`, { is_worker_manager: next });
      setIsManager(next);
      toast.success(next ? "Worker manager enabled — they can add expenses." : "Worker manager disabled.");
      onManagerToggled?.(next);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setManagerBusy(false);
    }
  };

  const submit = async () => {
    if (!worker) return;
    const em = email.trim().toLowerCase();
    if (!em) return toast.error("Email is required");
    if (!sendInvite && !password) {
      return toast.error("Set a temp password or check 'email an activation link'.");
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/mgmt/payroll_workers/${worker.id}/create_user`, {
        email: em,
        password: password || undefined,
        send_invite: sendInvite,
      });
      if (sendInvite) {
        if (data.email_sent) toast.success(`Invite email sent to ${em}`);
        else toast.warning(`Invite created but email failed${data.email_error ? ` (${data.email_error})` : ""}. Copy the link below.`);
      } else {
        toast.success("Login created with the temp password.");
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="max-w-md" data-testid="worker-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> {already ? "Update login" : "Create login"} — {worker?.name}
          </DialogTitle>
          <DialogDescription>
            {already ? (
              "This worker already has a login. You can update the email or reset their password."
            ) : (
              "Give this worker an account so they can be added as a ticket watcher (and log in later)."
            )}
          </DialogDescription>
        </DialogHeader>

        {already && (
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2">
            <CheckCircle2 className="w-4 h-4" /> Login active: <strong>{worker?.email}</strong>
          </div>
        )}

        {already && (
          <div className="flex items-center justify-between gap-3 border border-border rounded-sm px-3 py-2.5">
            <div className="flex items-start gap-2">
              <Receipt className="w-4 h-4 mt-0.5 text-muted-foreground" />
              <div>
                <div className="text-sm font-semibold">Worker manager</div>
                <div className="text-xs text-muted-foreground">
                  Normal worker account, plus they can add expenses to the Management USA NJ books.
                </div>
              </div>
            </div>
            <Switch
              checked={isManager}
              onCheckedChange={toggleManager}
              disabled={managerBusy}
              data-testid="worker-user-manager-toggle"
            />
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="worker@example.com"
              className="rounded-sm h-10"
              data-testid="worker-user-email"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
              data-testid="worker-user-invite"
            />
            Email them an activation link
          </label>
          <div>
            <Label>Or set a temp password (optional)</Label>
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave blank to use invite email"
              className="rounded-sm h-10"
              data-testid="worker-user-password"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !email.trim()} className="rounded-sm" data-testid="worker-user-submit">
            {saving ? "Saving…" : already ? "Update" : "Create login"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
